export enum Mode {
  TUTOR = "tutor",
  SOCRATES = "socrates",
}

/**
 * 채팅 메시지 (기본)
 */
export interface Message {
  id: string;
  group_id: string;
  content: string;
  sender: "user" | "ai"; // 발신자: 사용자 또는 AI
  created_at: string;
  is_loading?: boolean; // 로딩 상태
  error?: string; // 에러 메시지
  recommended_questions?: string[]; // AI 응답에서 받은 추천 질문 배열
  reference?: Reference[]; // 참고자료 배열
  isShowDetail?: boolean; // 참고자료 상세 패널 표시 여부
  metadata?: {
    source?: string; // 메시지 출처 (예: tutor, socrates)
    reference_ids?: string[]; // 참고자료 ID
  };
}

/**
 * 채팅 메시지 (Tutor.js 호환)
 * 원본 Tutor.js의 history 구조와 100% 호환되는 타입
 */
export interface ChatMessage {
  id: string;
  response_id: string;
  last_result?: any;
  recommended_questions: string[];
  latency: number;
  input_text: string;
  reference: Reference[];
  response: string;
  images: any[];
  action: string;
  sub_action?: string;
  question_count?: number;
  followup_count?: number;
  from_chat?: string;
  user_eva: {
    simple_eva: 0 | 1 | null;
    eva?: string;
  };
  introText?: IntroTextItem[];
  introList?: IntroTextItem[];
  token_count: number;
  ip_address?: string;
  is_open?: boolean;
  isShowDetail?: boolean;
}

/**
 * 스트림 이벤트 타입
 */
export type StreamEventType =
  | "ClarioStatus"
  | "ClarioResponse"
  | "ClarioMessageID";

export type StreamDataType =
  | "main"
  | "sub"
  | "DONE"
  | "FINISH"
  | "all"
  | "text";

/**
 * 스트림 청크
 */
export interface StreamChunk {
  type?: StreamDataType;
  text?: string;
  status_code?: number;
  id?: string;
}

/**
 * 스트림 완료 데이터
 */
export interface StreamCompleteData {
  type: "all";
  response_id: string;
  last_result: any;
  recommended_questions: string[];
  latency: number;
  input_text: string;
  reference: Reference[];
  response: string;
  images: any[];
  action: string;
  sub_action: string;
  question_count: number;
  followup_count: number;
  from_chat: string;
  user_eva: {
    simple_eva: 0 | 1 | null;
  };
  token_count?: number;
  user_pk?: string;
}

/**
 * Intro 텍스트 아이템 (구조화된 응답)
 */
export interface IntroTextItem {
  id: string;
  type: StreamDataType;
  text?: string;
}

/**
 * 사용자 평가 (피드백)
 */
export interface UserEvaluation {
  simple_eva: 0 | 1 | null;
  eva: string;
}

/**
 * 피드백 요청
 */
export interface FeedbackRequest {
  response_id: string;
  simple_eva: 0 | 1 | null;
  eva: string;
}

/**
 * 대화 히스토리 아이템
 */
export interface HistoryItem {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  preview: string; // 첫 메시지 프리뷰
  message_count: number;
  is_archived: boolean;
}

/**
 * 튜터 그룹 (채팅 그룹)
 */
export interface TutorGroup {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  chat_count?: number;
  is_completed?: boolean; // 소크라테스 면접 완료 여부
  isClicked?: boolean; // UI 상태
  isEdit?: boolean; // UI 상태
}

/**
 * 그룹 목록 페이지네이션
 */
export interface GroupListPagination {
  offset: number;
  limit: number;
  total: number;
}

/**
 * 제안 카드
 */
export interface SuggestionCard {
  id: string;
  title: string;
  description: string;
  icon?: string;
  category?: string;
}

/**
 * 검색 결과
 */
export interface SearchResult {
  id: string;
  content: string;
  type: "message" | "history" | "reference";
  highlighted_content?: string; // 하이라이트된 부분
}

/**
 * API 참고자료 (Reference)
 */
export interface Reference {
  url: string; // 참고자료 링크
  name: string; // 자료 제목
  medium: string; // 출처 (예: "과학동아", "동아사이언스")
  summary: string; // 요약 설명
  image: ReferenceImage[]; // 이미지 배열
}

/**
 * 참고자료 이미지
 */
export interface ReferenceImage {
  IMG_SRC: string; // 이미지 URL
  IMG_Description: string; // 이미지 설명
  IMG_TITLE: string; // 이미지 제목
}

/**
 * 샘플 질문 카테고리
 */
export interface SampleQuestion {
  id: number;
  user_id: string;
  icon: string; // 아이콘 이미지 경로
  flex: string; // CSS flex 값
  color: string; // 배경색 (헥스 코드)
  category: string; // 카테고리 이름
  rep_question: string; // 대표 질문
  updated_at: string; // 업데이트 시간
}

/**
 * 페르소나 선택 옵션
 */
export interface PersonaOption {
  label: string; // 표시 텍스트
  value: string; // 실제 값
}
